import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../api";
import type { Turbine, TurbineEvent } from "../types";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { fmtSlot } from "../utils/date";
import TurbineActions from "../components/TurbineActions";

interface Station {
  id: number;
  name: string;
  city: string;
  timezone: string;
}

type View = "cards" | "history";

export default function Turbines() {
  const { t } = useT();
  const navigate = useNavigate();
  const [view, setView] = useState<View>("cards");
  const [stations, setStations] = useState<Station[]>([]);
  const [turbines, setTurbines] = useState<Turbine[]>([]);
  const [recentByTurbine, setRecentByTurbine] = useState<Record<number, TurbineEvent[]>>({});
  const [history, setHistory] = useState<TurbineEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const load = async () => {
    setError(null);
    try {
      const [ss, tt] = await Promise.all([
        api.get<Station[]>("/api/stations"),
        api.get<Turbine[]>("/api/turbines"),
      ]);
      setStations(ss);
      setTurbines(tt);
      // Last 3 events per turbine (lazy: we fetch all here once for the cards)
      const evMap: Record<number, TurbineEvent[]> = {};
      await Promise.all(tt.map(async t => {
        try {
          evMap[t.id] = await api.get<TurbineEvent[]>(`/api/turbines/${t.id}/events?limit=3`);
        } catch {
          evMap[t.id] = [];
        }
      }));
      setRecentByTurbine(evMap);
    } catch (e) {
      setError((e as ApiError).message);
    }
  };

  const loadHistory = async () => {
    setError(null);
    setHistory(null);
    try {
      const list = await api.get<TurbineEvent[]>("/api/turbines/events?limit=50");
      setHistory(list);
    } catch (e) {
      setError((e as ApiError).message);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (view === "history") loadHistory();
  }, [view]);

  const toggleActive = async (tb: Turbine) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    try {
      await api.patch<Turbine>(`/api/turbines/${tb.id}`, { is_active: !tb.is_active });
      load();
    } catch (e) {
      setError((e as ApiError).message);
    }
  };

  const switchView = (v: View) => {
    tg()?.HapticFeedback?.selectionChanged?.();
    setView(v);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>{t("turbines.title")}</h1>
        <div className="period-tabs" style={{ margin: 0 }}>
          <button
            className={"period-tab" + (view === "cards" ? " active" : "")}
            onClick={() => switchView("cards")}
          >
            🏭 {t("turbines.tab_list")}
          </button>
          <button
            className={"period-tab" + (view === "history" ? " active" : "")}
            onClick={() => switchView("history")}
          >
            📜 {t("turbines.tab_history")}
          </button>
        </div>
      </div>

      {error && <div className="alert error">{error}</div>}

      {view === "cards" && stations.map(st => {
        const tbs = turbines.filter(tb => tb.station_id === st.id);
        return (
          <div key={st.id} style={{ marginBottom: 20 }}>
            <h2 style={{ marginBottom: 8 }}>
              🏭 {st.name}{" "}
              <span style={{ fontSize: 13, color: "var(--hint)", fontWeight: 400 }}>
                ({st.city})
              </span>
            </h2>
            {tbs.length === 0 && (
              <div className="empty-state">{t("turbines.none_for_station")}</div>
            )}
            {tbs.map(tb => {
              let stateDot = "❌";
              let stateText = t("turbines.state_stopped");
              let stateColor = "var(--destructive)";
              if (!tb.is_active) {
                stateDot = "⛔";
                stateText = t("turbines.state_off");
                stateColor = "var(--destructive)";
              } else if (tb.is_running) {
                stateDot = "🟢";
                stateText = t("turbines.state_running");
                stateColor = "#34c759";
              }
              const evs = recentByTurbine[tb.id] ?? [];
              const open = !!expanded[tb.id];
              return (
                <div key={tb.id} className="card">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="grow">
                      <div style={{ fontSize: 16, fontWeight: 600 }}>{tb.name}</div>
                      <div style={{ fontSize: 13, marginTop: 4, color: stateColor }}>
                        {stateDot} <b>{stateText}</b>
                      </div>
                    </div>
                    <button className="period-tab" onClick={() => toggleActive(tb)}>
                      {tb.is_active ? t("turbines.deactivate") : t("turbines.activate")}
                    </button>
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <TurbineActions turbine={tb} onChanged={load} />
                  </div>

                  {tb.is_active && (
                    <button
                      className={"big-btn " + (tb.is_running ? "primary" : "warning")}
                      style={{ marginTop: 8 }}
                      onClick={() => {
                        tg()?.HapticFeedback?.impactOccurred("light");
                        navigate(`/report/fill?turbine_id=${tb.id}&from=turbines`);
                      }}
                    >
                      <span className="big-btn-icon">{tb.is_running ? "📋" : "⚠"}</span>
                      <span className="big-btn-text">
                        <b>{t("turbines.fill_btn")}</b>
                        <small>
                          {tb.is_running ? t("turbines.fill_sub") : t("turbines.fill_sub_offline")}
                        </small>
                      </span>
                    </button>
                  )}

                  <button
                    type="button"
                    className="btn secondary disclosure"
                    style={{ marginTop: 12 }}
                    onClick={() => setExpanded(prev => ({ ...prev, [tb.id]: !open }))}
                  >
                    <span>{open ? "▴" : "▾"}</span>
                    <span style={{ flex: 1, textAlign: "start", marginLeft: 8 }}>
                      {t("turbines.recent_events")}
                    </span>
                    <span style={{ fontSize: 12, color: "var(--hint)" }}>{evs.length}</span>
                  </button>
                  {open && (
                    evs.length === 0 ? (
                      <div className="empty-state" style={{ padding: 12 }}>
                        {t("turbines.no_events")}
                      </div>
                    ) : (
                      <ul className="list" style={{ marginTop: 8, marginBottom: 0 }}>
                        {evs.map(ev => <EventRow key={ev.id} ev={ev} />)}
                      </ul>
                    )
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {view === "history" && (
        <>
          {!history && !error && (
            <div className="empty-state" style={{ padding: 40 }}>
              <span className="spinner" />
            </div>
          )}
          {history && history.length === 0 && (
            <div className="empty-state">{t("turbines.no_events")}</div>
          )}
          {history && history.length > 0 && (
            <ul className="list">
              {history.map(ev => <EventRow key={ev.id} ev={ev} showTurbine />)}
            </ul>
          )}
        </>
      )}
    </div>
  );
}


function EventRow({ ev, showTurbine }: { ev: TurbineEvent; showTurbine?: boolean }) {
  const { t } = useT();
  const cls = ev.event_type === "start" ? "event-start" : "event-stop";
  // Start events post-migration carry KV; legacy Start rows still have KW.
  // Stop events always carry KW.
  let readout = "";
  if (ev.event_type === "start") {
    if (ev.kv !== null && ev.kv !== undefined) readout = ` · KV ${ev.kv}`;
    else if (ev.kw !== null && ev.kw !== undefined) readout = ` · KW ${ev.kw}`;
  } else if (ev.kw !== null && ev.kw !== undefined) {
    readout = ` · KW ${ev.kw}`;
  }
  return (
    <li className={`list-item event-row ${cls}`} style={{ padding: "10px 12px" }}>
      <div className="grow" style={{ fontSize: 13 }}>
        <div>
          <span style={{ marginRight: 6 }}>
            {ev.event_type === "start" ? "▶" : "⏹"}
          </span>
          <b>{ev.event_type === "start" ? t("turbines.evt_start") : t("turbines.evt_stop")}</b>
          {" · "}
          {fmtSlot(ev.event_time)}
          {readout}
        </div>
        {showTurbine && (ev.turbine_name || ev.station_name) && (
          <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 2 }}>
            {ev.station_name && <>{ev.station_name} · </>}
            <b>{ev.turbine_name}</b>
          </div>
        )}
        {ev.reason && (
          <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 2 }}>{ev.reason}</div>
        )}
      </div>
    </li>
  );
}
